import { FC, memo, useMemo } from 'react';
import {
  DataObjectType,
  DataVersionStatus,
  Detail,
  formatDate,
  formatDateTimeShort,
  VersionTag,
  Table,
  TableCell,
  TextEllipsis,
} from '@atlas-frontend/sdk';
import { AppLink } from '../../common/AppLink';
import { VersionSpan } from '../output-data/VersionCell';
import { createColumnHelper } from '@tanstack/react-table';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';
import styled from 'styled-components';
import { colorGray } from '@atlas-frontend/design-tokens';
import translations from './DataConflictsTable.translations.json';
import {
  ConflictRow,
  DataObjectColumnData,
  TimeParams,
  VersionInfo,
} from './utils';

const { addTranslations, t } = i18nNsFactory('data-conflicts-table');
addTranslations(translations);

const columnHelper = createColumnHelper<ConflictRow>();

interface DataConflictsTableProps {
  data: ConflictRow[];
}

export const DataConflictsTable: FC<DataConflictsTableProps> = ({ data }) => {
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'sequenceNumber',
        header: () => t('column.sequenceNumber'),
        meta: { width: '6ch' },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'dataObjectA',
        header: () => t('column.dataObjectA'),
        meta: { width: '24ch' },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'timeParamsA',
        header: () => t('column.timeParamsA'),
        meta: { width: '20ch' },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'versionA',
        header: () => t('column.versionA'),
        meta: { width: '24ch' },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'dataObjectB',
        header: () => t('column.dataObjectB'),
        meta: { width: '24ch' },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'timeParamsB',
        header: () => t('column.timeParamsB'),
        meta: { width: '20ch' },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'versionB',
        header: () => t('column.versionB'),
        meta: { width: '24ch' },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'dataObjectF',
        header: () => t('column.dataObjectF'),
        meta: { width: '24ch' },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'timeParamsF',
        header: () => t('column.timeParamsF'),
        meta: { width: '20ch' },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'versionF',
        header: () => t('column.versionF'),
        meta: { width: '24ch' },
        enableSorting: false,
        enableColumnFilter: false,
      }),
    ],
    [],
  );

  return (
    <TableContainer>
      <Table
        columns={columns}
        data={data}
        name="data-conflicts-table"
        columnOrderingDisabled
        columnResizeDisabled
        renderBody={(rows) =>
          rows.map((row) => {
            const conflict = row.original;
            const span = conflict.timeParamsA.length;

            return conflict.timeParamsA.map((_, i) => {
              const isLast = i === span - 1;
              const borderColor = isLast ? undefined : colorGray[2];

              return (
                <tr key={`${row.id}-${i}`}>
                  {i === 0 && (
                    <>
                      <StyledTableCell rowSpan={span}>
                        {conflict.sequenceNumber}
                      </StyledTableCell>
                      <StyledTableCell rowSpan={span}>
                        <DataObjectCell dataObject={conflict.dataObjectA} />
                      </StyledTableCell>
                    </>
                  )}
                  <StyledTableCell style={{ borderBottomColor: borderColor }}>
                    <TimeParamsCell params={conflict.timeParamsA[i]} />
                  </StyledTableCell>
                  <StyledTableCell style={{ borderBottomColor: borderColor }}>
                    <VersionCell version={conflict.versionA[i]} />
                  </StyledTableCell>
                  <StyledTableCell style={{ borderBottomColor: borderColor }}>
                    {conflict.dataObjectB[i] ? (
                      <DataObjectCell dataObject={conflict.dataObjectB[i]!} />
                    ) : null}
                  </StyledTableCell>
                  <StyledTableCell style={{ borderBottomColor: borderColor }}>
                    {conflict.timeParamsB[i] ? (
                      <TimeParamsCell params={conflict.timeParamsB[i]!} />
                    ) : null}
                  </StyledTableCell>
                  <StyledTableCell style={{ borderBottomColor: borderColor }}>
                    {conflict.versionB[i] ? (
                      <VersionCell version={conflict.versionB[i]!} />
                    ) : null}
                  </StyledTableCell>
                  {i === 0 && (
                    <>
                      <StyledTableCell rowSpan={span}>
                        {conflict.dataObjectF ? (
                          <DataObjectCell dataObject={conflict.dataObjectF} />
                        ) : null}
                      </StyledTableCell>
                      <StyledTableCell rowSpan={span}>
                        {conflict.timeParamsF ? (
                          <TimeParamsCell params={conflict.timeParamsF} />
                        ) : null}
                      </StyledTableCell>
                      <StyledTableCell rowSpan={span}>
                        <VersionCell version={conflict.versionF} />
                      </StyledTableCell>
                    </>
                  )}
                </tr>
              );
            });
          })
        }
      />
    </TableContainer>
  );
};

const DataObjectCell: FC<{ dataObject: DataObjectColumnData }> = ({
  dataObject,
}) => (
  <TextEllipsis>
    {dataObject.code} - {dataObject.name}
  </TextEllipsis>
);

type TimeParamsCellProps =
  | TimeParams
  | {
      valueDate: string | null;
      calculationTime: string;
      timeLink: string | null;
    };

const TimeParamsCell: FC<{ params: TimeParamsCellProps }> = memo(
  ({ params }) => {
    const formattedTime = params.calculationTime
      ? formatDateTimeShort(params.calculationTime)
      : null;
    const timeLink = 'timeLink' in params ? params.timeLink : null;
    return (
      <Flex vertical gap={2}>
        <Detail label={t('label.valueDate')}>
          {params.valueDate ? formatDate(params.valueDate) : null}
        </Detail>
        <Detail label={t('label.time')}>
          {formattedTime &&
            (timeLink ? (
              <AppLink id="time-link" to={timeLink} target="_blank">
                {formattedTime}
              </AppLink>
            ) : (
              formattedTime
            ))}
        </Detail>
      </Flex>
    );
  },
);

const VersionCell: FC<{ version: VersionInfo | ConflictRow['versionF'] }> = ({
  version,
}) => {
  if (!version?.statusCode) return null;
  const statusCode = version.statusCode as DataVersionStatus;
  const showVersionNumber =
    !('dataObjectType' in version) ||
    version.dataObjectType !== DataObjectType.IN;

  return (
    <VersionTag className={statusCode}>
      <Flex gap="small" align="center">
        {showVersionNumber && version.versionNumber != null && (
          <VersionSpan>
            {`${version.versionNumber}${version.minorNumber ? `.${version.minorNumber}` : ''}`}
          </VersionSpan>
        )}
        <DataVersionStatus status={statusCode} />
      </Flex>
    </VersionTag>
  );
};

const StyledTableCell = styled(TableCell)`
  vertical-align: middle;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;
